import * as React from 'react';
import { useSelector } from 'react-redux';
import { AppBar, Toolbar, Container, Typography, Box, Link } from '@mui/material';
import http from 'Utils/http';

const { version } = require('../../../package.json');

const LARAVEL_VERSION = '3.9.7';
const SYMFONY_VERSION = '3.5.4';

const REDIS_MEMORY_REFRESH_MS = 60000;
const WORKERS_REFRESH_MS = 1000;
const MB = 1048576;
const ALERT_COLOR = '#ef5350';
const WARNING_COLOR = '#ffa726';
const OK_COLOR = '#66bb6a';
const LINES_PER_COLUMN = 3;

const lineSx = { fontSize: '0.75rem', lineHeight: 1.4, whiteSpace: 'nowrap' };

const percentColor = (percent) => {
    if (percent > 75) {
        return ALERT_COLOR;
    }

    return percent >= 50 ? WARNING_COLOR : OK_COLOR;
};

const busyPercent = (busy, workers) => (workers > 0 ? Math.round((busy / workers) * 100) : 0);

const queueColor = ({ workers, busy, messages }) => {
    const color = percentColor(busyPercent(busy, workers));

    return color === OK_COLOR && messages > 0 ? WARNING_COLOR : color;
};

const signed = (value) => (value > 0 ? `+${value}` : `${value}`);

const RedisMemory = () => {
    const [memory, setMemory] = React.useState(null);
    const [growth, setGrowth] = React.useState({});
    const [monitoring, setMonitoring] = React.useState(false);
    const previous = React.useRef(null);

    React.useEffect(() => {
        const load = () => http.get('/redis/memory')
            .then((res) => {
                const now = Date.now();
                const last = previous.current;

                setGrowth(Object.fromEntries(Object.entries(res.data.queues || {}).map(([queue, { messages }]) => [
                    queue,
                    last && messages !== null && last.queues[queue]?.messages != null
                        ? Math.round(((messages - last.queues[queue].messages) / ((now - last.time) / 1000)) * 10) / 10
                        : 0,
                ])));
                previous.current = { queues: res.data.queues || {}, time: now };
                setMemory(res.data);
            })
            .catch(() => setMemory(null));

        load();
        const timer = setInterval(load, monitoring ? WORKERS_REFRESH_MS : REDIS_MEMORY_REFRESH_MS);

        return () => clearInterval(timer);
    }, [monitoring]);

    if (!memory || memory.percent === null) {
        return null;
    }

    const queues = Object.entries(memory.queues || {});
    const queueColumns = [];

    for (let i = 0; i < queues.length; i += LINES_PER_COLUMN) {
        queueColumns.push(queues.slice(i, i + LINES_PER_COLUMN));
    }

    return (
        <Box sx={{ display: 'flex', gap: 3 }}>
            <Box>
                <Typography sx={{ ...lineSx, color: percentColor(memory.percent) }}>
                    Redis {Math.round(memory.used / MB)} / {Math.round(memory.max / MB)} MB ({memory.percent}%)
                </Typography>
                {memory.monitoring && (
                    <Link component="button" sx={{ ...lineSx, display: 'block' }} onClick={() => setMonitoring(!monitoring)}>
                        {monitoring ? 'Stop monitoring workers' : 'Monitor workers'}
                    </Link>
                )}
            </Box>
            {monitoring && (
                <Box>
                    <Typography sx={{ ...lineSx, color: percentColor(busyPercent(memory.active_flows, memory.max_flows)) }}>
                        Active Flows: {memory.active_flows} ({memory.max_flows})
                    </Typography>
                </Box>
            )}
            {monitoring && queueColumns.map((column) => (
                <Box key={column[0][0]}>
                    {column.map(([queue, stats]) => (
                        <Typography key={queue} sx={{ ...lineSx, color: queueColor(stats) }}>
                            {queue}: {stats.workers}w {stats.busy} busy ({busyPercent(stats.busy, stats.workers)}%) {stats.messages ?? '-'} msg ({signed(growth[queue] ?? 0)}/s)
                        </Typography>
                    ))}
                </Box>
            ))}
        </Box>
    );
};

const Footer = () => {
    const year = new Date().getFullYear();
    const access = useSelector((state) => state.auth.user?.access);

    React.useEffect(() => {
        if (!version || localStorage.getItem('version') === version) {
            return;
        }
        sessionStorage.clear();
        localStorage.setItem('version', version);
    }, []);

    return (
        <AppBar position="static" component="footer">
            <Container maxWidth={false}>
                <Toolbar disableGutters>
                    {(access === 'Admin' || access === 'Company Admin') && <RedisMemory />}
                    <Typography
                        sx={{
                            marginLeft: 'auto',
                            fontWeight: 600,
                        }}
                    >
                        Version {version} / {LARAVEL_VERSION} / {SYMFONY_VERSION}
                        <br/>© Infintegration, 2022 - {year}
                    </Typography>
                </Toolbar>
            </Container>
        </AppBar>
    );
};
export default Footer;
